import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowRight, 
  Shield, 
  Layers,
  Bot,
  ChevronRight,
  Check,
  Loader2,
  Sparkles,
  FileText,
  Lock,
  Gift,
  MessageCircle,
  Settings,
  Workflow,
  Lightbulb
} from 'lucide-react';
import { ucpPhases } from '../data/mock';
import { DonutChart } from '../components/charts/DonutChart';
import { UCPFlowDiagram, UCPComparisonDiagram } from '../components/UCPDiagram';
import PacerLogo from '../components/PacerLogo';

const HomePage = () => {
  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const navigate = useNavigate();

  // Block Gmail and Outlook emails
  const blockedDomains = ['gmail.com', 'outlook.com', 'hotmail.com', 'live.com', 'googlemail.com'];

  const validateEmail = (email) => {
    if (!email) return true;
    const domain = email.split('@')[1]?.toLowerCase();
    if (domain && blockedDomains.includes(domain)) {
      setEmailError('Gmail and Outlook email addresses not accepted');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail.includes('@')) {
      validateEmail(newEmail);
    } else {
      setEmailError('');
    }
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!url || !email) return;
    
    if (!validateEmail(email)) return;
    
    setIsAnalyzing(true);
    setAnalysisStatus('Starting analysis...');
    
    setTimeout(() => {
      navigate('/results', { state: { url, email } });
    }, 500);
  };

  // Partner brands marquee
  const marqueeItems = [
    "The Body Shop",
    "Universal Works",
    "Stripe & Stare",
    "MG Motors",
    "Smart Salem",
    "Linjer",
    "Copper Pearl"
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <PacerLogo width={100} height={28} className="md:w-[100px] w-[80px]" />
          <nav className="hidden md:flex items-center gap-8">
            <a href="#how-it-works" className="text-sm text-[#f5f5f0]/60 hover:text-[#f5f5f0] transition-colors">How it works</a>
            <a href="#phases" className="text-sm text-[#f5f5f0]/60 hover:text-[#f5f5f0] transition-colors">UCP Phases</a>
            <a href="https://pacer.studio" target="_blank" rel="noopener noreferrer" className="text-sm text-[#f5f5f0]/60 hover:text-[#f5f5f0] transition-colors">What We Do</a>
          </nav>
          <Button 
            variant="outline" 
            className="border-[#f5f5f0]/20 text-[#f5f5f0] hover:bg-[#f5f5f0] hover:text-[#0a0a0a] transition-all text-sm md:text-base"
            onClick={() => document.getElementById('checker').scrollIntoView({ behavior: 'smooth' })}
          >
            Check Your Store
          </Button>
        </div>
      </header>

      {/* Hero Section with gradient */}
      <section className="pt-28 md:pt-32 pb-16 md:pb-20 px-4 md:px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 via-transparent to-transparent pointer-events-none"></div>
        <div className="max-w-5xl mx-auto relative">
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#f5f5f0]/5 rounded-full text-xs text-[#f5f5f0]/60 border border-[#f5f5f0]/10">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
              Powered by Universal Commerce Protocol
            </span>
          </div>
          <h1 className="text-4xl md:text-7xl font-light leading-[1.1] tracking-tight mb-6 md:mb-8">
            Is your store ready for
            <span className="block mt-2 text-[#f5f5f0]/40">agentic commerce?</span>
          </h1>
          <p className="text-lg md:text-xl text-[#f5f5f0]/50 max-w-2xl mb-10 md:mb-12 leading-relaxed">
            AI agents are the new shoppers. They discover, negotiate, and transact on behalf of buyers. 
            Find out if your store speaks their language.
          </p>

          {/* Checker Form */}
          <div id="checker" className="bg-gradient-to-br from-[#111111] to-[#0d0d0d] rounded-xl md:rounded-2xl p-6 md:p-8 border border-white/5 shadow-2xl">
            <form onSubmit={handleAnalyze} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#f5f5f0]/40 mb-2">Your Store URL</label>
                  <Input
                    type="text"
                    placeholder="https://yourstore.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 h-12 rounded-lg focus:border-[#f5f5f0]/30"
                    required
                    data-testid="url-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#f5f5f0]/40 mb-2">Work Email Address</label>
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={handleEmailChange}
                    className={`bg-[#0a0a0a] border-white/10 text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 h-12 rounded-lg focus:border-[#f5f5f0]/30 ${emailError ? 'border-red-500/50' : ''}`}
                    required
                    data-testid="email-input"
                  />
                  {emailError && (
                    <p className="text-red-400 text-sm mt-2" data-testid="email-error">{emailError}</p>
                  )}
                </div>
              </div>
              
              <Button 
                type="submit"
                disabled={isAnalyzing}
                className="w-full bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0] h-12 px-8 rounded-lg font-medium transition-all"
                data-testid="analyse-btn"
              >
                {isAnalyzing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {analysisStatus || 'Analysing...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Analyse Store
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Partner Brands Marquee */}
      <div className="border-y border-white/5 py-4 md:py-5 overflow-hidden">
        <div className="flex items-center justify-center gap-4 mb-3">
          <span className="text-[10px] text-[#f5f5f0]/30 uppercase tracking-[0.2em]">Partner to leading brands</span>
        </div>
        <div className="marquee-container">
          <div className="marquee-content">
            {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, i) => (
              <span key={i} className="flex items-center gap-6 md:gap-8 mx-6 md:mx-10">
                <span className="text-base md:text-lg text-[#f5f5f0]/50 whitespace-nowrap font-light tracking-wide">{item}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/60"></span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Shopify Advantage Section with gradient */}
      <section className="py-16 md:py-24 px-4 md:px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/8 via-emerald-500/3 to-transparent pointer-events-none"></div>
        <div className="max-w-5xl mx-auto relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-sm text-emerald-400 uppercase tracking-widest font-medium">The Shopify Advantage</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-light mb-4 md:mb-6">
            Shopify merchants start
            <span className="text-emerald-400"> ahead</span>
          </h2>
          <p className="text-[#f5f5f0]/50 mb-8 md:mb-12 max-w-2xl">
            Shopify was a co-architect of the Universal Commerce Protocol. As a Shopify merchant, you already have 
            critical UCP infrastructure built into your store—automatically.
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-8">
            {/* Automatic Feature 1 */}
            <div className="bg-gradient-to-br from-[#111111] to-[#0d0d0d] rounded-xl p-5 md:p-6 border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-400 font-medium uppercase tracking-wider">Automatic</span>
              </div>
              <h3 className="font-medium mb-2">Product Schema</h3>
              <p className="text-sm text-[#f5f5f0]/50">
                Every Shopify product page automatically includes Schema.org JSON-LD markup for prices, availability, and images.
              </p>
            </div>
            
            {/* Automatic Feature 2 */}
            <div className="bg-gradient-to-br from-[#111111] to-[#0d0d0d] rounded-xl p-5 md:p-6 border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-400 font-medium uppercase tracking-wider">Automatic</span>
              </div>
              <h3 className="font-medium mb-2">Commerce APIs</h3>
              <p className="text-sm text-[#f5f5f0]/50">
                Cart API, Product API, and Checkout flows are built-in. AI agents can programmatically manage transactions.
              </p>
            </div>
            
            {/* Automatic Feature 3 */}
            <div className="bg-gradient-to-br from-[#111111] to-[#0d0d0d] rounded-xl p-5 md:p-6 border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-400 font-medium uppercase tracking-wider">Automatic</span>
              </div>
              <h3 className="font-medium mb-2">Policy Pages</h3>
              <p className="text-sm text-[#f5f5f0]/50">
                Shipping, returns, and privacy policies are auto-generated at standardised URLs AI agents expect.
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-[#111111] to-[#0d0d0d] rounded-xl p-5 md:p-6 border border-amber-500/10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h4 className="font-medium mb-2">But that's just the foundation</h4>
                <p className="text-sm text-[#f5f5f0]/50 leading-relaxed">
                  While Shopify gives you a head start, the real competitive advantage comes from 
                  <span className="text-[#f5f5f0]/80"> UCP Extensions</span>—custom capabilities like loyalty programmes, 
                  bundle negotiators, and consultative selling agents. These make your brand 
                  <span className="text-emerald-400"> uniquely attractive</span> to AI shopping agents, 
                  not just compatible with them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section with gradient */}
      <section className="py-16 md:py-24 px-4 md:px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-500/3 to-transparent pointer-events-none"></div>
        <div className="max-w-5xl mx-auto relative">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <span className="text-sm text-[#f5f5f0]/40 uppercase tracking-widest mb-4 block">The Shift</span>
              <h2 className="text-3xl md:text-4xl font-light mb-6 leading-tight">
                Commerce is being
                <span className="text-[#f5f5f0]/40"> rewritten</span>
              </h2>
              <p className="text-[#f5f5f0]/50 leading-relaxed mb-6">
                Google, Shopify, and the world's largest retailers have co-developed the Universal Commerce Protocol. 
                AI agents now discover products, negotiate terms, and complete transactions—all programmatically.
              </p>
              <p className="text-[#f5f5f0]/50 leading-relaxed">
                Stores that don't speak UCP become invisible to AI shoppers. 
                Your competitors who do? They become the default answer.
              </p>
            </div>
            <div className="bg-[#111111] rounded-xl md:rounded-2xl p-6 md:p-8 border border-white/5">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#f5f5f0]/5 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-[#f5f5f0]/60" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">AI Agent Query</h4>
                    <p className="text-sm text-[#f5f5f0]/40">"Find me a sensitive skin moisturiser with SPF 30+"</p>
                  </div>
                </div>
                <div className="h-px bg-white/5"></div>
                <div className="flex items-start gap-4 opacity-40">
                  <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-400 text-xs">✗</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Without UCP</h4>
                    <p className="text-sm text-[#f5f5f0]/40">Agent can't parse your products. You're invisible.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">With UCP</h4>
                    <p className="text-sm text-[#f5f5f0]/40">Your brand is the definitive answer. Transaction complete.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How UCP Works - Diagram Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto">
          <span className="text-sm text-[#f5f5f0]/40 uppercase tracking-widest mb-4 block">How UCP Works</span>
          <h2 className="text-3xl md:text-4xl font-light mb-4 md:mb-6">The agentic commerce flow</h2>
          <p className="text-[#f5f5f0]/50 mb-8 md:mb-12 max-w-2xl">
            When a customer asks an AI assistant to find a product, the Universal Commerce Protocol enables 
            seamless discovery, negotiation, and checkout—all without leaving the conversation.
          </p>
          
          <div className="bg-[#111111] rounded-xl md:rounded-2xl p-5 md:p-8 border border-white/5 mb-6 md:mb-8">
            <UCPFlowDiagram />
          </div>

          <UCPComparisonDiagram />
        </div>
      </section>

      {/* What We Check - Redesigned with brand colours */}
      <section id="how-it-works" className="py-16 md:py-24 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <span className="text-sm text-[#f5f5f0]/40 uppercase tracking-widest mb-4 block">What We Check</span>
          <h2 className="text-3xl md:text-4xl font-light mb-4">Your UCP readiness audit</h2>
          <p className="text-[#f5f5f0]/50 mb-8 md:mb-12 max-w-2xl">
            We analyse your store across three critical dimensions that determine AI agent compatibility.
          </p>
          
          {/* Mobile-first card layout */}
          <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-6">
            {/* Basic Setup */}
            <div className="bg-gradient-to-b from-emerald-500/10 to-transparent rounded-xl md:rounded-2xl p-5 md:p-6 border border-emerald-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Layers className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-medium text-emerald-400">Basic Setup</h3>
                  <p className="text-xs text-[#f5f5f0]/40">Foundation</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-emerald-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">UCP Profile</span>
                    <p className="text-xs text-[#f5f5f0]/40">/.well-known/ucp endpoint</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-emerald-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">llms.txt</span>
                    <p className="text-xs text-[#f5f5f0]/40">AI agent instructions</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Bot className="w-4 h-4 text-emerald-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">Crawler Access</span>
                    <p className="text-xs text-[#f5f5f0]/40">robots.txt configuration</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Trust Signals */}
            <div className="bg-gradient-to-b from-amber-500/10 to-transparent rounded-xl md:rounded-2xl p-5 md:p-6 border border-amber-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-medium text-amber-400">Trust Signals</h3>
                  <p className="text-xs text-[#f5f5f0]/40">GXO Optimisation</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Lock className="w-4 h-4 text-amber-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">Landed Cost</span>
                    <p className="text-xs text-[#f5f5f0]/40">Guaranteed pricing</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-amber-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">Policies</span>
                    <p className="text-xs text-[#f5f5f0]/40">Machine-readable JSON-LD</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">Verification</span>
                    <p className="text-xs text-[#f5f5f0]/40">Business identity proof</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Extensions - Updated messaging */}
            <div className="bg-gradient-to-b from-blue-500/10 to-transparent rounded-xl md:rounded-2xl p-5 md:p-6 border border-blue-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-medium text-blue-400">Extensions</h3>
                  <p className="text-xs text-[#f5f5f0]/40">Brand Control</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Gift className="w-4 h-4 text-blue-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">Loyalty & Offers</span>
                    <p className="text-xs text-[#f5f5f0]/40">Points, bundles, discounts</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MessageCircle className="w-4 h-4 text-blue-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">Consultative</span>
                    <p className="text-xs text-[#f5f5f0]/40">Quizzes, expert handoff</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Workflow className="w-4 h-4 text-blue-400/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm text-[#f5f5f0]/80">Custom Workflows</span>
                    <p className="text-xs text-[#f5f5f0]/40">Checkout, fulfilment logic</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Extensions explanation */}
          <div className="mt-8 md:mt-12 bg-[#111111] rounded-xl p-5 md:p-6 border border-white/5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Settings className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-medium mb-2">Why Extensions Matter</h4>
                <p className="text-sm text-[#f5f5f0]/50 leading-relaxed">
                  Without extensions, you're relying on the standard Shopify handshake—letting Google and AI decide how your brand is sold. 
                  Extensions let you incorporate <span className="text-[#f5f5f0]/80">loyalty programmes, custom bundle configurations, consultation quizzes, bespoke checkout flows</span>, and more. 
                  This is how you maintain brand control in the agentic era.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Score Visualisation with Donuts */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto">
          <span className="text-sm text-[#f5f5f0]/40 uppercase tracking-widest mb-4 block">Example Analysis</span>
          <h2 className="text-3xl md:text-4xl font-light mb-4 md:mb-8">UCP Readiness Distribution</h2>
          <p className="text-[#f5f5f0]/50 mb-8 md:mb-12 max-w-2xl">
            Most stores score poorly on UCP Extensions because they haven't implemented custom capability agents. 
            This is your competitive advantage.
          </p>
          
          <div className="bg-[#111111] rounded-xl md:rounded-2xl p-5 md:p-8 border border-white/5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              <DonutChart value={85} label="Basic Setup" sublabel="score" size={120} strokeWidth={10} />
              <DonutChart value={62} label="Semantic Layer" sublabel="score" size={120} strokeWidth={10} />
              <DonutChart value={45} label="GXO Signals" sublabel="score" size={120} strokeWidth={10} />
              <DonutChart value={12} label="UCP Extensions" sublabel="score" size={120} strokeWidth={10} />
            </div>
            <p className="text-center text-xs md:text-sm text-[#f5f5f0]/40 mt-6 md:mt-8">
              Average scores across 500+ Shopify stores analysed
            </p>
          </div>
        </div>
      </section>

      {/* UCP Phases */}
      <section id="phases" className="py-16 md:py-24 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <span className="text-sm text-[#f5f5f0]/40 uppercase tracking-widest mb-4 block">The Roadmap</span>
          <h2 className="text-3xl md:text-4xl font-light mb-8 md:mb-16">Three phases to UCP readiness</h2>
          
          <div className="space-y-4 md:space-y-8">
            {ucpPhases.map((phase, i) => (
              <div key={i} className="bg-[#111111] rounded-xl md:rounded-2xl p-5 md:p-8 border border-white/5 hover:border-white/10 transition-colors">
                <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#f5f5f0] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-[#0a0a0a] font-bold">{phase.phase}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 mb-2">
                      <h3 className="text-xl md:text-2xl font-medium">{phase.title}</h3>
                      <span className="text-sm text-[#f5f5f0]/40">— {phase.subtitle}</span>
                    </div>
                    <p className="text-[#f5f5f0]/50 mb-4 text-sm md:text-base">{phase.problem}</p>
                    <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                      <span className="text-xs text-[#f5f5f0]/40 uppercase tracking-wider">Deliverable</span>
                      <p className="text-[#f5f5f0] font-medium text-sm md:text-base">{phase.deliverable}</p>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {phase.features.map((feature, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-[#f5f5f0]/60">
                          <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm text-emerald-400/80 italic">"{phase.outcome}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-[#0d0d0d]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-light mb-4 md:mb-6">Ready to become AI-discoverable?</h2>
          <p className="text-[#f5f5f0]/50 mb-6 md:mb-8 text-base md:text-lg">
            Get your free UCP readiness score and learn exactly what you need to do to win in the agentic commerce era.
          </p>
          <Button 
            onClick={() => document.getElementById('checker').scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0] h-12 px-8 rounded-lg font-medium"
          >
            <span className="flex items-center gap-2">
              Check Your Store Now
              <ArrowRight className="w-4 h-4" />
            </span>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <PacerLogo width={80} height={24} />
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-sm text-[#f5f5f0]/40">
            <a href="https://ucp.dev" target="_blank" rel="noopener noreferrer" className="hover:text-[#f5f5f0] transition-colors">UCP Specification</a>
            <a href="https://shopify.engineering/ucp" target="_blank" rel="noopener noreferrer" className="hover:text-[#f5f5f0] transition-colors">Shopify Engineering</a>
            <a href="https://developers.google.com/merchant/ucp" target="_blank" rel="noopener noreferrer" className="hover:text-[#f5f5f0] transition-colors">Google UCP</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
